import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import type { FrontendApi } from '@ory/client-fetch'
import { useConfig } from '@/lib/ConfigProvider'
import { onUnauthenticated } from '@/lib/api'
import { oryClient } from './ory'
import { fetchSession } from './session'
import type { SessionState } from './session'

interface SessionContextValue {
  readonly state: SessionState
  readonly ory: FrontendApi
  /** Re-ask Ory. Used after a completed login flow and on a 401 from our API. */
  readonly refresh: () => Promise<void>
  readonly logout: () => Promise<void>
}

/*
  Exported for the browser-test harness (#151), which mounts the real component
  tree with a stubbed API and session. Those are the INPUTS to a layout test;
  everything below them is the subject.

  Exported rather than re-implemented on purpose: seven browser-only defects in
  one day each needed a harness built by hand and thrown away, and a copy of a
  provider is a copy that drifts from the thing it is standing in for.

  `browser.html` is served in development and never built, so nothing that reads
  this reaches `dist/` — `check-invariants.sh` fails the run if it ever does.
*/
// eslint-disable-next-line react-refresh/only-export-components
export const SessionContext = createContext<SessionContextValue | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const config = useConfig()
  const ory = useMemo(() => oryClient(config), [config])
  const [state, setState] = useState<SessionState>({ status: 'loading' })

  // Guards against a slow first whoami resolving after a faster refresh and
  // overwriting it with stale state — the classic way a signed-in user gets
  // bounced to login a second after arriving.
  const generation = useRef(0)

  const refresh = useCallback(async () => {
    const mine = ++generation.current
    const next = await fetchSession(ory)
    if (generation.current === mine) setState(next)
  }, [ory])

  useEffect(() => {
    void refresh()
  }, [refresh])

  // A 401 from our own API means the backend disagrees with what we think the
  // session is — it verifies the session itself at its edge, and it is the
  // authority. Re-asking Ory rather than assuming anonymous keeps "signed out"
  // and "provider unreachable" distinct even on this path.
  useEffect(() => onUnauthenticated(() => void refresh()), [refresh])

  const logout = useCallback(async () => {
    let revoked = false
    try {
      const flow = await ory.createBrowserLogoutFlow()
      await ory.updateLogoutFlow({ token: flow.logout_token })
      revoked = true
    } catch {
      // Deliberately NOT treated as a logout.
      //
      // This used to clear local state regardless, on the reasoning that Ory
      // being unreachable must not strand someone who asked to leave. But
      // clearing local state does not end the session — the cookie is still
      // valid, so the screen says "signed out" and the next `whoami` says
      // otherwise. On a shared machine that is someone walking away from a
      // wallet they believe they have left (#29).
      //
      // Saying "we could not sign you out" is the lesser harm in both
      // directions: it is true, and it prompts the one action that does work,
      // which is closing the browser.
    }
    generation.current++
    if (revoked) {
      setState({ status: 'anonymous' })
      return
    }
    // Ask who we actually are rather than asserting. If the cookie survived,
    // this answers `authenticated` and the customer can see the button did not
    // work; if Ory is unreachable it answers `unavailable`, which is also true
    // and is a state this app already renders honestly.
    await refresh()
  }, [ory, refresh])

  const value = useMemo<SessionContextValue>(
    () => ({ state, ory, refresh, logout }),
    [state, ory, refresh, logout],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext)
  if (ctx === null) throw new Error('useSession must be used inside a SessionProvider')
  return ctx
}
